import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../partials/Header';
import Footer from '../../partials/Footer';
import PageIllustration from '../../partials/PageIllustration';
import { importer } from 'ipfs-unixfs-importer';
import { inspectCid, gatewayUrl } from '../../utils/ipfs';

const PROFILES = [
  {
    id: 'unixfs-v1-2025',
    label: 'Modern — CIDv1 (default in current Kubo/Helia)',
    flags: '--cid-version=1',
    desc: 'CIDv1 with raw leaf blocks — what a current ipfs add produces.',
  },
  {
    id: 'unixfs-v0-2015',
    label: 'Classic — CIDv0 (legacy ipfs add)',
    flags: '--cid-version=0',
    desc: 'CIDv0 ("Qm…") with dag-pb leaves — matches older ipfs add output.',
  },
];

const READ_CHUNK = 4 * 1024 * 1024; // read file in 4 MiB slices; importer sub-chunks to 256 KiB

const blockstore = () => ({
  _m: new Map(),
  async get(k) { const b = this._m.get(k.toString()); if (!b) throw new Error('block not found'); return b; },
  async put(k, v) { this._m.set(k.toString(), v); },
  async has(k) { return this._m.has(k.toString()); },
});

async function* fileStream(file, onProgress) {
  let offset = 0;
  while (offset < file.size) {
    const end = Math.min(offset + READ_CHUNK, file.size);
    yield new Uint8Array(await file.slice(offset, end).arrayBuffer());
    offset = end;
    onProgress(offset / file.size);
  }
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MiB`;
  return `${(n / 1024 ** 3).toFixed(2)} GiB`;
}

function IpfsCidGenerator() {
  const [mode, setMode] = useState('file');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [profile, setProfile] = useState(PROFILES[0].id);
  const [wrap, setWrap] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hashing, setHashing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const canCompute = mode === 'file' ? !!file : text.length > 0;

  const compute = async () => {
    if (!canCompute) { setError(mode === 'file' ? 'Choose a file first' : 'Enter some text first'); return; }
    setError('');
    setResult(null);
    setHashing(true);
    setProgress(0);
    try {
      const name = mode === 'file' ? file.name : 'input.txt';
      const size = mode === 'file' ? file.size : new TextEncoder().encode(text).length;
      const content = mode === 'file'
        ? fileStream(file, setProgress)
        : (async function* () { yield new TextEncoder().encode(text); setProgress(1); })();

      let last;
      for await (const entry of importer(
        [{ path: name, content }],
        blockstore(),
        { onlyHash: true, profile, wrapWithDirectory: wrap }
      )) {
        last = entry;
      }
      setProgress(1);
      const info = inspectCid(last.cid.toString());
      setResult({ cid: last.cid.toString(), info, size, name });
    } catch (e) {
      setError(e.message || 'Failed to compute CID');
    } finally {
      setHashing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setResult(null); }
  };

  const cliCommand = () => {
    const p = PROFILES.find((x) => x.id === profile);
    const name = mode === 'file' && file ? file.name : 'input.txt';
    return `ipfs add ${p.flags}${wrap ? ' -w' : ''} "${name}"`;
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>IPFS CID Generator — Hash Files Like ipfs add</title>
        <meta name="description" content="Compute the IPFS CID your file will get — same chunking and hashing as ipfs add, with CIDv0/CIDv1 and wrap-in-directory options. Free, in-browser, no upload." />
        <meta name="keywords" content="ipfs cid generator, ipfs hash, calculate ipfs cid, file to cid, ipfs add cid, unixfs hash, cid calculator, ipfs tools" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.ogtechnologies.co/tools/ipfs-cid-generator/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ogtechnologies.co/tools/ipfs-cid-generator/" />
        <meta property="og:title" content="IPFS CID Generator — Hash Files Like ipfs add" />
        <meta property="og:description" content="Compute the IPFS CID your file will get — same chunking and hashing as ipfs add. Free, in-browser, no upload." />
        <meta property="og:image" content="https://www.ogtechnologies.co/og-og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.ogtechnologies.co/tools/ipfs-cid-generator/" />
        <meta name="twitter:title" content="IPFS CID Generator — Hash Files Like ipfs add" />
        <meta name="twitter:description" content="Compute the IPFS CID your file will get — same chunking and hashing as ipfs add. Free, in-browser, no upload." />
        <meta name="twitter:image" content="https://www.ogtechnologies.co/og-og-image.png" />
      </Helmet>

      <Header />

      <main className="grow">
        <div className="relative max-w-6xl mx-auto h-0 pointer-events-none" aria-hidden="true">
          <PageIllustration />
        </div>

        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="pt-32 pb-12 md:pt-40 md:pb-20">
              <div className="max-w-3xl mx-auto text-center pb-12 md:pb-16">
                <h1 className="h1">IPFS CID Generator — File → CID</h1>
                <p className="text-xl text-gray-400 mt-4">
                  Compute the Content Identifier a file will get on IPFS — the same UnixFS chunking and sha2-256 hashing
                  as <code className="text-purple-300">ipfs add</code>, without running a node. Files never leave your browser.
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                  <div className="flex gap-2 mb-4">
                    {['file', 'text'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          mode === m ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        onClick={() => { setMode(m); setResult(null); setError(''); }}
                      >
                        {m === 'file' ? 'File Input' : 'Text Input'}
                      </button>
                    ))}
                  </div>

                  {mode === 'file' ? (
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={onDrop}
                      onClick={() => inputRef.current?.click()}
                      className={`mb-4 border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                        dragging ? 'border-purple-500 bg-purple-900/20' : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        ref={inputRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setResult(null); } }}
                      />
                      {file ? (
                        <div>
                          <p className="text-gray-200 font-medium">{file.name}</p>
                          <p className="text-gray-500 text-sm">{formatBytes(file.size)} — click to choose a different file</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-gray-300 font-medium">Drop a file here, or click to browse</p>
                          <p className="text-gray-500 text-sm mt-1">Hashed locally — nothing is uploaded</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-gray-300 text-sm font-medium mb-1">Text content</label>
                      <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={4}
                        className="w-full bg-gray-700 text-white px-4 py-2 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none font-mono text-sm"
                        placeholder="Text to address by CID (UTF-8 encoded)"
                      />
                      <p className="text-xs text-gray-500 mt-1">Encoded as UTF-8 and hashed as a single file.</p>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-1">Import profile</label>
                      <select
                        value={profile}
                        onChange={(e) => setProfile(e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none text-sm"
                      >
                        {PROFILES.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">{PROFILES.find((p) => p.id === profile).desc}</p>
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={wrap}
                          onChange={(e) => setWrap(e.target.checked)}
                          className="mt-1 accent-purple-600"
                        />
                        <span>
                          Wrap in a directory (<code className="text-purple-300">ipfs add -w</code>)
                          <span className="block text-xs text-gray-500">Root CID addresses a folder containing the file — useful for gateway URLs ending in a filename.</span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={compute}
                    disabled={hashing || !canCompute}
                    className="w-full px-4 py-3 rounded-md bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
                  >
                    {hashing ? `Hashing… ${Math.round(progress * 100)}%` : 'Compute CID'}
                  </button>

                  {hashing && (
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-4 overflow-hidden">
                      <div className="bg-purple-500 h-2 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
                    </div>
                  )}

                  {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

                  {result && (
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-300 text-sm font-medium">CID</span>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(result.cid)}
                            className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors"
                          >
                            Copy
                          </button>
                        </div>
                        <div className="bg-gray-700 rounded-md px-4 py-3">
                          <code className="text-purple-300 font-mono text-sm break-all">{result.cid}</code>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                          <p className="text-gray-500 text-xs mb-1">Version / codec</p>
                          <p className="text-gray-200 font-mono">v{result.info.version} · {result.info.codec}</p>
                        </div>
                        <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                          <p className="text-gray-500 text-xs mb-1">Hash</p>
                          <p className="text-gray-200 font-mono">{result.info.hashFunction}</p>
                        </div>
                        <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                          <p className="text-gray-500 text-xs mb-1">Input size</p>
                          <p className="text-gray-200 font-mono">{formatBytes(result.size)}</p>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                        <p className="text-gray-500 text-xs mb-1">Equivalent CLI</p>
                        <code className="text-gray-300 font-mono text-sm break-all">{cliCommand()}</code>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <a
                          href={gatewayUrl('ipfs.io', result.cid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300"
                        >
                          View via ipfs.io →
                        </a>
                        <Link to={`/tools/ipfs-gateway-checker/`} className="text-purple-400 hover:text-purple-300">
                          Check gateway availability →
                        </Link>
                        <Link to={`/tools/ipfs-cid-inspector/`} className="text-purple-400 hover:text-purple-300">
                          Inspect this CID →
                        </Link>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 text-xs text-gray-500 space-y-1">
                    <p>The CID is deterministic: same content + same import profile = same CID. Chunker: fixed 256 KiB, layout: balanced, hash: sha2-256.</p>
                    <p>To make the content retrievable you still need to <em>provide</em> it — pin the file on a pinning service or your own node, then it will load at any gateway.</p>
                  </div>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  More IPFS tools:{' '}
                  <Link to="/tools/ipfs-cid-inspector/" className="text-purple-400 hover:text-purple-300">CID Inspector</Link>
                  {' · '}
                  <Link to="/tools/ipfs-gateway-checker/" className="text-purple-400 hover:text-purple-300">Gateway Checker</Link>
                  {' · '}
                  <Link to="/tools/ipfs-url-converter/" className="text-purple-400 hover:text-purple-300">URL Converter</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default IpfsCidGenerator;
